type PlainlyErrorCode =
    | "RENDER_INVALID"
    | "WORKER_CONVERT_ASSET_FAILED"
    | "WORKER_FFMPEG_ENCODING_ERROR"
    | "WORKER_DOWNLOAD_ASSET_FAILED"
    | "WORKER_IMPORT_ASSET_FAILED"
    | "WORKER_SCRIPT_ACTION_WRONG_LAYER_TYPE"
    | "WORKER_SCRIPT_ROOT_COMP_NOT_ALLOWED"
    | "WORKER_SCRIPT_LAYER_NOT_FOUND"
    | "WORKER_SCRIPT_COMPOSITION_NOT_FOUND"
    | "WORKER_FONT_INSTALL_FAILED"
    | "WORKER_SCRIPT_FONTS_INVALID";

type Asset = {
    src: string;
    error: string;
    fileName: string;
};

type PlainlyError = {
    code: PlainlyErrorCode;
    message: string;
    params: {
        layers?: {
            compositionId: string;
            compositionName: string;
            font?: string;
            fontLocation?: string;
            layerId?: string;
            layerName?: string;
            text?: string;
        }[];
        assets?: string[] | Asset[];
        urls?: string[];
    };
};

export default function aeHandleErrors(error: string | Error) {
    const baseErrorMessage = "There was an unexpected error while rendering the AE template: ";

    if (typeof error !== "string") {
        return baseErrorMessage + error.message;
    }

    const parsedError = JSON.parse(error.replace("Plainly error : ", "")) as PlainlyError;

    const { code, message, params } = parsedError;

    switch (code) {
        case "RENDER_INVALID": {
            let errorMessage = `${baseErrorMessage}<br/>${message}`;

            errorMessage +=
                "<br/><br/>This error occurs when the rendering cannot be processed because the parameters provided for the render are invalid. (e.g. invalid font, invalid media item, etc.)";

            return errorMessage;
        }

        case "WORKER_CONVERT_ASSET_FAILED": {
            return `${message}<br/><br/><strong>Assets:</strong> ${(params.assets as string[])?.join(", ")}`;
        }

        case "WORKER_DOWNLOAD_ASSET_FAILED": {
            return `${message}<br/><br/><strong>Assets:</strong> ${params.urls?.join(", ")}`;
        }

        case "WORKER_IMPORT_ASSET_FAILED": {
            const affectedAssets = (params.assets as Asset[])?.map(asset => {
                const assetName = asset.fileName ?? "unknown";
                const assetSrc = asset.src ?? "unknown";
                const errorMessage = asset.error ?? "unknown";

                return `<strong>Asset:</strong> ${assetName}<br/><strong>Src:</strong> ${assetSrc}<br/><strong>Error:</strong> ${errorMessage}`;
            });

            return `${message}<br/><br/><strong>Assets:</strong> ${affectedAssets?.join("<br/><br/>")}`;
        }

        case "WORKER_SCRIPT_FONTS_INVALID": {
            const affectedLayers = params.layers?.map(layer => {
                const layerName = layer.layerName ?? "unknown";
                const compositionName = layer.compositionName ?? "unknown";
                const font = layer.font ?? "unknown";

                return `<strong>Layer:</strong> ${layerName}<br/><strong>Composition:</strong> ${compositionName}<br/><strong>Font:</strong> ${font}`;
            });

            const message = `Could not set font for some text layers.<br/><br/> Please make sure the characters used are supported by the font.<br/><br/> ${affectedLayers?.join("<br/><br/>")}`;

            return message;
        }
        default:
            return baseErrorMessage + message;
    }
}
